import React, { useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, Copy, Gift, Share2, Sparkles, Users, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/common/Button';
import { database } from '../services/firebase';
import { ref, onValue, off } from 'firebase/database';
import { useTranslation } from 'react-i18next';
import { getAuthRedirectPath } from '../utils/authRedirect';

const heroShellClass = 'relative overflow-hidden rounded-3xl border border-primary-100 bg-gradient-to-br from-white via-primary-50 to-amber-50 text-gray-950 shadow-sm dark:border-white/10 dark:bg-gray-950 dark:bg-none dark:text-white dark:shadow-xl';
const heroGlowClass = 'absolute inset-0 bg-gradient-to-br from-primary-500/10 via-accent-500/10 to-amber-400/15 dark:from-primary-500/30 dark:via-accent-500/25 dark:to-amber-400/10';
const heroChipClass = 'mb-2 inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-white/80 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-700 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:text-primary-100 dark:shadow-none';
const heroPanelClass = 'rounded-2xl border border-primary-100 bg-white/75 p-3 text-gray-950 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:text-white dark:shadow-none';
const heroButtonClass = 'rounded-2xl border border-primary-100 bg-white/80 text-primary-700 hover:bg-white dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:bg-white/20';
const heroStatCardClass = 'rounded-2xl border border-primary-100 bg-white/70 px-3 py-2.5 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:shadow-none';

const parseDate = (dateStr) => {
  if (!dateStr) return new Date(0);
  try {
    if (dateStr.includes(':') && dateStr.includes(' ')) {
      const [datePart, timePart] = dateStr.split(' ');
      const parts = datePart.split(':');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        return new Date(`${year}-${month}-${day}T${timePart}`);
      }
    }
    return new Date(dateStr);
  } catch (e) {
    return new Date(0);
  }
};

const formatDisplayDate = (dateStr, t) => {
  if (!dateStr) return t('refer.unknown_date', 'Unknown Date');
  try {
    const [datePart, timePart] = dateStr.split(' ');
    if (!datePart || !timePart) return dateStr;

    const [day, month, fullYear] = datePart.split(':');
    let [hours, minutes] = timePart.split(':');
    const shortYear = fullYear.slice(-2);
    const ampm = parseInt(hours, 10) >= 12 ? 'pm' : 'am';
    hours = parseInt(hours, 10) % 12;
    hours = hours || 12;
    const formattedHours = String(hours).padStart(2, '0');

    return t('refer.joined_on', 'Joined on {{date}} at {{time}}', {
      date: `${day}/${month}/${shortYear}`,
      time: `${formattedHours}:${minutes} ${ampm}`,
    });
  } catch (e) {
    return dateStr;
  }
};

const ReferAndEarn = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);

  const referralCode = (user?.referralCode || '').trim();
  const settings = useSelector((state) => state.settings ? state.settings.settings : {});
  const referralPercent = settings?.referralBonus || 10;
  const universalLink = settings?.universalScriptLink || '';

  const referralLink = useMemo(() => {
    if (!referralCode) return '';
    if (universalLink && universalLink.startsWith('https://t.me/')) {
      const separator = universalLink.includes('?') ? '&' : '?';
      return `${universalLink}${separator}startapp=ref_${referralCode}`;
    }
    return `https://t.me/Psh_store_bot/app?startapp=ref_${referralCode}`;
  }, [referralCode, universalLink]);

  useEffect(() => {
    if (!referralCode) {
      setLoading(false);
      return;
    }

    const referralsRef = ref(database, `referrals-codes/${referralCode}/referrals`);
    const listener = onValue(referralsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.values(data).sort((a, b) => parseDate(b.joinedAt) - parseDate(a.joinedAt));
        setReferrals(list);
      } else {
        setReferrals([]);
      }
      setLoading(false);
    });

    return () => off(referralsRef, 'value', listener);
  }, [referralCode]);

  const copyValue = async (value, label, labelLower = label.toLowerCase()) => {
    if (!value) {
      return toast.error(t('refer.no_value_available', 'No {{label}} available.', { label: labelLower }));
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
        toast.success(t('refer.copy_success', '{{label}} copied!', { label }));
        return;
      }
      throw new Error('Clipboard API unavailable');
    } catch (err) {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = value;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (successful) {
          toast.success(t('refer.copy_success', '{{label}} copied!', { label }));
        } else {
          throw new Error('Fallback copy failed');
        }
      } catch (fallbackErr) {
        console.error('Copy failed:', fallbackErr);
        toast.error(t('refer.copy_failed', 'Unable to copy {{label}}. Manual copy required.', { label: labelLower }));
      }
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('refer.share_title', 'Join PSH Store'),
          text: t('refer.share_text', 'Use my code {{code}} to get rewards!', { code: referralCode }),
          url: referralLink,
        });
      } catch (err) {
        // user cancelled or failed
      }
    } else {
      copyValue(referralLink, t('refer.referral_link', 'Referral link'), t('refer.referral_link_lower', 'referral link'));
    }
  };

  const approvedCount = referrals.filter((item) => item.reward === 'approved').length;
  const pendingCount = referrals.filter((item) => !item.reward || item.reward === 'pending').length;

  if (!isAuthenticated) return <Navigate to={getAuthRedirectPath()} replace />;

  return (
    <div className="min-h-screen bg-gray-50/80 py-4 dark:bg-gray-950 sm:py-6">
      <div className="container mx-auto max-w-5xl px-3 sm:px-4">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <section className={heroShellClass}>
            <div className={heroGlowClass} />
            <div className="relative p-4 sm:p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <div className={heroChipClass}>
                    <Sparkles className="h-3.5 w-3.5 text-amber-300" />
                    {t('refer.program_label', 'Refer & Earn Program')}
                  </div>
                  <h1 className="text-2xl font-black tracking-normal sm:text-3xl">{t('refer.title', 'Invite Friends')}</h1>
                  <p className="mt-1 max-w-xl text-sm font-medium text-gray-600 dark:text-gray-300">
                    {t('refer.description', 'Get')} <span className="font-black text-amber-300">{referralPercent}% {t('refer.commission', 'Commission')}</span> {t('refer.description_suffix', 'on their first order.')}
                  </p>
                </div>

                <div className="grid min-w-0 gap-2 sm:min-w-[360px]">
                  <div className={heroPanelClass}>
                    <p className="mb-1 text-[10px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('refer.your_code', 'Your Code')}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate font-mono text-xl font-black tracking-wider notranslate" translate="no">{referralCode || '...'}</span>
                      <button
                        onClick={() => copyValue(referralCode, t('refer.code', 'Code'), t('refer.code_lower', 'code'))}
                        className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-gray-950 transition hover:bg-gray-100 active:scale-95"
                        aria-label={t('refer.copy_code_aria', 'Copy referral code')}
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleShare}
                      size="sm"
                      className="flex-1 rounded-2xl bg-gradient-to-r from-accent-500 to-amber-500 text-white shadow-lg shadow-accent-900/20"
                    >
                      <Share2 className="mr-2 h-4 w-4" />
                      {t('refer.invite_friend', 'Invite Friend')}
                    </Button>
                    <Button
                      onClick={() => copyValue(referralLink, t('refer.link', 'Link'), t('refer.link_lower', 'link'))}
                      variant="ghost"
                      size="sm"
                      className={heroButtonClass}
                      aria-label={t('refer.copy_link_aria', 'Copy referral link')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2">
                {[
                  { label: t('refer.total', 'Total'), value: referrals.length, icon: Users },
                  { label: t('refer.approved', 'Approved'), value: approvedCount, icon: CheckCircle },
                  { label: t('refer.pending', 'Pending'), value: pendingCount, icon: Clock },
                ].map((stat) => {
                  const Icon = stat.icon;
                  return (
                    <div key={stat.label} className={heroStatCardClass}>
                      <div className="mb-1 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        <Icon className="h-3.5 w-3.5" />
                        {stat.label}
                      </div>
                      <p className="text-lg font-black text-gray-950 dark:text-white notranslate" translate="no">{stat.value}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-3 lg:grid-cols-[0.85fr_1.15fr]">
            <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-5">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-widest text-primary-600 dark:text-primary-400">
                    {t('refer.how_it_works', 'How it works')}
                  </p>
                  <h2 className="mt-1 text-lg font-black text-gray-900 dark:text-white">
                    {t('refer.quick_steps', '{{count}} quick steps', { count: 3 })}
                  </h2>
                </div>
                <Gift className="h-5 w-5 text-amber-500" />
              </div>
              <div className="space-y-2">
                {[
                  { step: '1', title: t('refer.step1_title', 'Share invite link'), desc: t('refer.step1_desc', 'Send your unique link to friends') },
                  { step: '2', title: t('refer.step2_title', 'Friend signs up'), desc: t('refer.step2_desc', 'They create an account using your code') },
                  { step: '3', title: t('refer.step3_title', 'Earn Rewards'), desc: `${t('refer.step3_desc', 'You get')} ${referralPercent}% ${t('refer.step3_desc_suffix', 'of their first order value')}` },
                ].map((item) => (
                  <div key={item.step} className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-950">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-sm font-black text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                      {item.step}
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-black text-gray-900 dark:text-white">{item.title}</h3>
                      <p className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-5">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-widest text-accent-600 dark:text-accent-400">
                    {t('refer.recent_referrals', 'Recent Referrals')}
                  </p>
                  <h2 className="mt-1 text-lg font-black text-gray-900 dark:text-white">
                    {t('refer.referral_activity', 'Referral Activity')}
                  </h2>
                </div>
                <Users className="h-5 w-5 text-accent-500" />
              </div>

              {loading ? (
                <div className="rounded-2xl border border-gray-100 bg-gray-50 py-8 text-center text-sm font-bold text-gray-400 dark:border-gray-800 dark:bg-gray-950">
                  {t('common.loading', 'Loading...')}
                </div>
              ) : referrals.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center dark:border-gray-700 dark:bg-gray-950">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-gray-400 dark:bg-gray-900">
                    <Users className="h-6 w-6" />
                  </div>
                  <p className="text-sm font-black text-gray-900 dark:text-white">
                    {t('refer.no_referrals', 'No referrals yet')}
                  </p>
                  <p className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t('refer.no_referrals_hint', 'Share your link to start earning.')}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {referrals.map((item, idx) => (
                    <motion.div
                      key={`${item.name || 'user'}-${item.joinedAt || idx}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-950"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary-100 to-accent-100 text-sm font-black uppercase text-primary-700 dark:from-gray-800 dark:to-gray-700 dark:text-gray-200">
                          {item.name?.[0] || t('refer.user_initial', 'U')}
                        </div>
                        <div className="min-w-0">
                          <h3 className="truncate text-sm font-black text-gray-900 dark:text-white">{item.name || t('refer.user_fallback', 'User')}</h3>
                          <p className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">{formatDisplayDate(item.joinedAt, t)}</p>
                        </div>
                      </div>

                      {item.reward === 'approved' ? (
                        <div className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-black text-green-700 dark:bg-green-900/25 dark:text-green-300">
                          <CheckCircle className="h-3.5 w-3.5" />
                          <span>+{item.pointsAwarded || t('refer.done', 'Done')}</span>
                        </div>
                      ) : item.reward === 'rejected' ? (
                        <div className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-black text-red-700 dark:bg-red-900/25 dark:text-red-300">
                          <XCircle className="h-3.5 w-3.5" />
                          <span>{t('refer.ineligible', 'Ineligible')}</span>
                        </div>
                      ) : (
                        <div className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-black text-amber-700 dark:bg-amber-900/25 dark:text-amber-300">
                          <Clock className="h-3.5 w-3.5" />
                          <span>{t('refer.pending', 'Pending')}</span>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default ReferAndEarn;
